/**
 * Instagram API utility functions
 * 
 * This file contains functions to fetch Instagram posts using our server-side API
 * which now returns static images from the public directory.
 */

// API base URL - use relative path in production and full URL in development
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'https://kalahats.com/api';

/**
 * Fetch Instagram posts using our server-side API
 * 
 * @param {string} username - Instagram username
 * @param {number} count - Number of posts to fetch
 * @returns {Promise<Array>} - Array of Instagram posts
 */
export const fetchInstagramPosts = async (username, count = 8) => {
  try {
    // Use our server-side API endpoint to fetch Instagram data
    const response = await fetch(`${API_BASE_URL}/instagram?username=${username}&count=${count}`);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const posts = await response.json();
    return posts;
  } catch (error) {
    console.error('Error fetching Instagram posts:', error);
    
    // Fallback to static images if the API fails
    console.log('Falling back to static placeholder images');
    return generatePlaceholderPosts(username, count);
  }
};

/**
 * Generate placeholder posts if the API fails
 * 
 * @param {string} username - Instagram username
 * @param {number} count - Number of posts to generate
 * @returns {Array} - Array of placeholder posts
 */
const generatePlaceholderPosts = (username, count) => {
  return Array.from({ length: Math.min(count, 4) }, (_, index) => ({
    id: `static-${index + 1}`,
    media_url: `/images/instagram/${index + 1}.jpg`,
    permalink: `https://www.instagram.com/${username}/`,
    caption: "Kala Hats - Handcrafted in Bali",
    timestamp: new Date().toISOString(),
    username: username
  }));
};
